/**
 * InternalCategory — the internal (`@a2zradiology.ai` staff, non-admin)
 * role's per-category page (`/labeling-overview/:category` when the
 * resolved role is "internal"). Read-only: nested worklist rows for every
 * annotator in every project, plus each project's shared golden-GT
 * worklist, **stopping at the worklist level** — no per-case list, no
 * approve/config controls. Markup mirrors mockup `#s-icat`
 * (PLANS/reviews/2026-07-07-labeling-overview-redesign-mockup.html).
 *
 * # Data sources
 * `useFactoryProjectsQuery()` for the project list, then the shared
 * `useEnrollmentsByProject` fan-out (`./useEnrollmentsByProject.ts`) over
 * `GET /projects/{pid}/enrollments` — also used by `AdminControlRoom`
 * (extracted in Task 11 to stop the two files duplicating it verbatim).
 *
 * `ProjectWorklistGroup` (Task 5) is NOT reused here: it's typed against
 * `MyEnrollment` (the CALLER'S OWN enrollment — carries `project_name` /
 * `worklist_project_id`, which only `GET /my-enrollments` returns) and
 * every row is an `onClick`-driven `<button>` with no read-only mode.
 * This view instead needs the ALL-ANNOTATOR `Enrollment` shape from
 * `GET /projects/{pid}/enrollments` (no `project_name`, no
 * `worklist_project_id`), and its rows must be inert, never clickable.
 * Making `ProjectWorklistGroup` support both shapes would mean editing a
 * file this task isn't scoped to touch, so a small local static group is
 * built below instead, reusing the same shared primitives.
 *
 * # Backend-access reality (design spec §6)
 * `GET /projects/{pid}/enrollments` is admin-gated on the backend today —
 * a non-admin internal caller's request may 403 (or otherwise error). If
 * ANY project's enrollments call errors (or the projects list itself
 * errors), this renders ONE explicit "ask an admin" empty state for the
 * whole page rather than a partially-populated or silently-empty list.
 * No silent fallback, no fake rows.
 *
 * TODO(backend): widen GET enrollments read-only to the internal domain
 * (spec §6).
 *
 * # No fabricated data
 * - TODO(category): factory `Project` has no `project_type` field, so the
 *   project list below can't be reliably scoped to `categoryKey` yet — the
 *   same gap `AdminControlRoom.tsx` / `ProjectsTable.tsx` document.
 *   Guessing a mapping from `task` (the only string `Project` carries)
 *   would be a silently wrong signal, so every registered project is
 *   shown here regardless of category.
 * - `Enrollment` has no done/total case counts — an `active` row gets the
 *   same binary 0/1 `ProgressMeter` `ProjectWorklistGroup` uses (never a
 *   fabricated fraction); `pending_approval` / `blocked_pool_empty` rows
 *   show a `StatusBadge` instead, matching mockup `#s-icat` exactly (the
 *   mockup's "62 / 100" style numbers are fixture data for the static
 *   mockup only, not real fields on `Enrollment`).
 * - A project's GT row only renders when `project.gt_annotator` is
 *   actually set — there's no field asserting "a GT worklist exists"
 *   otherwise, so rendering it unconditionally would be a fabricated row.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useFactoryProjectsQuery } from "../../../hooks/useFactoryQueries";
import type { Enrollment, EnrollmentState, Project } from "../../../types/factory";
import { CATEGORIES, type LabelingCategoryKey } from "../../../utils/labelingCategories";
import { categoryAccent } from "./categoryAccent";
import {
  CountStrip,
  LoadingPanel,
  ProgressMeter,
  SectionLabel,
  SquareList,
  SquareRow,
  StatusBadge,
} from "./primitives";
import type { StatusTone } from "./primitives";
import { useEnrollmentsByProject } from "./useEnrollmentsByProject";

const STATE_SUB_SUFFIX: Partial<Record<EnrollmentState, string>> = {
  blocked_pool_empty: "blocked (pool empty)",
};

/** Non-active states render a `StatusBadge` instead of a meter — there's no
 *  real progress number for any state, but a badge at least communicates
 *  something true (vs. a fabricated fraction). `active` returns `null` so
 *  the caller falls through to the binary meter. */
function stateBadge(state: EnrollmentState): { tone: StatusTone; label: string } | null {
  if (state === "pending_approval") return { tone: "sig", label: "Pending review" };
  if (state === "blocked_pool_empty") return { tone: "warn", label: "Blocked" };
  return null;
}

interface InternalProjectGroupProps {
  project: Project;
  enrollments: Enrollment[];
}

function InternalProjectGroup({ project, enrollments }: InternalProjectGroupProps) {
  const annotatorCount = new Set(enrollments.map((e) => e.annotator)).size;

  return (
    <SquareList>
      <SquareRow>
        <span aria-hidden="true" className="font-mono text-fg-faint">
          ◪
        </span>
        <span className="text-sm font-semibold text-fg">{project.name}</span>
        <span className="font-mono text-xs text-fg-muted">{project.project_id}</span>
        <span className="flex-1" />
        <span className="font-mono text-xs text-fg-muted">
          {annotatorCount} annotator{annotatorCount === 1 ? "" : "s"}
        </span>
      </SquareRow>

      {project.gt_annotator && (
        <div
          data-testid="gt-row"
          className="flex w-full items-center gap-4 py-3.5 pl-8 pr-5 text-left"
        >
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 shrink-0 rounded-sharp bg-border-strong"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-fg">{project.project_id}-gt-shared</div>
            <div className="text-xs text-fg-muted">Shared golden-GT set · internal labeling</div>
          </div>
          <StatusBadge tone="success">GT</StatusBadge>
        </div>
      )}

      {enrollments.map((e) => {
        const badge = stateBadge(e.state);
        const suffix = STATE_SUB_SUFFIX[e.state];
        return (
          <div
            key={`${e.project_id}-${e.annotator}`}
            data-testid="worklist-row"
            className="flex w-full items-center gap-4 py-3.5 pl-8 pr-5 text-left"
          >
            <span
              aria-hidden="true"
              className={`h-1.5 w-1.5 shrink-0 rounded-sharp ${
                e.state === "active" ? "bg-signal" : "bg-border-strong"
              }`}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-fg">
                {e.current_batch_id ? `Batch ${e.current_batch_id}` : "No batch assigned yet"}
              </div>
              <div className="text-xs text-fg-muted">
                {e.annotator} · Phase {e.phase}
                {suffix ? ` · ${suffix}` : ""}
              </div>
            </div>
            {badge ? (
              <StatusBadge tone={badge.tone}>{badge.label}</StatusBadge>
            ) : (
              <ProgressMeter done={0} total={1} tone="active" />
            )}
          </div>
        );
      })}
    </SquareList>
  );
}

interface InternalCategoryProps {
  categoryKey: LabelingCategoryKey;
}

export function InternalCategory({ categoryKey }: InternalCategoryProps) {
  const navigate = useNavigate();
  const meta = CATEGORIES.find((c) => c.key === categoryKey)!;
  const accent = categoryAccent(categoryKey);

  const projectsQuery = useFactoryProjectsQuery();
  const projects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data]);
  const projectIds = useMemo(() => projects.map((p) => p.project_id), [projects]);

  const enrollmentsResults = useEnrollmentsByProject(projectIds);

  const enrollmentsByPid = useMemo(() => {
    const map: Record<string, Enrollment[] | undefined> = {};
    projectIds.forEach((pid, i) => {
      map[pid] = enrollmentsResults[i]?.data;
    });
    return map;
  }, [projectIds, enrollmentsResults]);

  // Backend-access reality (spec §6): GET /projects/{pid}/enrollments is
  // admin-gated today, so a non-admin internal caller may 403. Treat ANY
  // project's enrollments call erroring (or the projects list itself
  // erroring) as "this tier isn't enabled for me yet" and degrade to one
  // explicit message — never a partially-populated or silently-wrong list.
  const accessDenied = projectsQuery.isError || enrollmentsResults.some((r) => r.isError);

  // Distinct from "0 projects" — while either query is still in flight,
  // `projects.length === 0` would otherwise render the empty-state copy
  // one tick early on every load.
  const isLoading = projectsQuery.isLoading || enrollmentsResults.some((r) => r.isLoading);

  const totalWorklists = useMemo(
    () =>
      projects.reduce((sum, p) => {
        const enrollments = enrollmentsByPid[p.project_id] ?? [];
        return sum + enrollments.length + (p.gt_annotator ? 1 : 0);
      }, 0),
    [projects, enrollmentsByPid],
  );

  return (
    <div className="h-full overflow-auto text-white page-premium">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={() => navigate("/labeling-overview")}
          className="mb-4 text-xs text-fg-muted transition hover:text-fg"
        >
          ← Labeling
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className={`w-0.5 self-stretch rounded-sharp ${accent.edge}`}
            />
            <h1 className="text-2xl font-bold text-fg">{meta.name}</h1>
          </div>
          <div className="mt-3">
            <CountStrip
              items={
                accessDenied
                  ? [`${projects.length} projects`]
                  : [
                      `${projects.length} projects`,
                      `${totalWorklists} worklists`,
                      "all annotators + shared GT",
                    ]
              }
            />
          </div>
        </div>

        {isLoading ? (
          <LoadingPanel label="Loading projects…" />
        ) : accessDenied ? (
          <div
            data-testid="internal-access-denied"
            className="rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted"
          >
            Ask an admin to enable the internal view for this project.
          </div>
        ) : (
          <>
            <SectionLabel eyebrow="All projects" note="every annotator + shared GT · read-only" />
            {projects.length === 0 ? (
              <div className="rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted">
                No projects yet
              </div>
            ) : (
              <div className="flex flex-col gap-3.5">
                {projects.map((project) => (
                  <InternalProjectGroup
                    key={project.project_id}
                    project={project}
                    enrollments={enrollmentsByPid[project.project_id] ?? []}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default InternalCategory;
